import { useEffect, useState } from 'react';
import { BuilderComponent, builder } from '@builder.io/react';
import { BUILDER_API_KEY } from '../../config/builder';

// Initialize Builder with your API key
builder.init(BUILDER_API_KEY);

interface BuilderTestProps {
  // Optional: pass custom data to your Builder component
  customData?: Record<string, unknown>;
}

/**
 * BuilderTest Component
 *
 * This component fetches and renders the "test" component from Builder.io
 *
 * Usage:
 * <BuilderTest />
 *
 * Or with custom data:
 * <BuilderTest customData={{ userName: "John" }} />
 */
export const BuilderTest = ({ customData }: BuilderTestProps) => {
  const [content, setContent] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Fetch the "Test" page using its URL path from Builder.io
        const builderContent = await builder
          .get('page', {
            url: '/figma-imports',
            userAttributes: {
              urlPath: '/figma-imports',
            },
          })
          .promise();

        setContent(builderContent);
      } catch (err) {
        console.error('Error fetching Builder content:', err);
        setError('Failed to load Builder.io content');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 min-h-screen bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white p-8">
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700">{error}</div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-white p-8">
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-700">
          No content found. Make sure it&apos;s published in Builder.io.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <BuilderComponent model="page" content={content} data={customData} />
    </div>
  );
};

export default BuilderTest;
